import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Camera, Clock, RotateCcw } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { canExecuteChecklist } from '../../lib/services/selfMonitoring'
import { getAssignments, getVisitChecklist } from '../../lib/services/visits'
import { getFieldAppUrl } from '../../lib/fieldAppHost'

const RECURRING_DOMAINS = new Set(['IC', 'LSC', 'MED', 'ENV', 'SPD'])

function isRecurringTask(standardId: string): boolean {
  const domain = standardId.split('-')[0]?.toUpperCase() ?? ''
  return RECURRING_DOMAINS.has(domain)
}

export function SelfMonitoringChecklistPage() {
  const { viewerContext } = useAuth()
  const role = viewerContext?.role || 'Surveyor'

  const visitsQuery = useQuery({
    queryKey: ['self-monitoring-checklist-visits'],
    queryFn: getAssignments,
  })

  const selectedVisitId = (visitsQuery.data || [])[0]?.id
  const checklistQuery = useQuery({
    queryKey: ['self-monitoring-checklist-items', selectedVisitId],
    queryFn: () => getVisitChecklist(selectedVisitId!),
    enabled: !!selectedVisitId,
  })

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="app-surface p-5">
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Self-Monitoring Checklist Execution</h1>
        <p className="mt-1 text-sm text-slate-600">Checklist interactions are constrained by role and visit state. Items marked with a recurring icon require periodic re-verification.</p>
      </header>

      <section className="app-surface p-5">
        {visitsQuery.isLoading && <p className="text-sm text-slate-500">Loading scoped visits...</p>}
        {visitsQuery.error && <p className="text-sm text-red-600">{(visitsQuery.error as Error).message}</p>}

        {!visitsQuery.isLoading && !visitsQuery.error && !selectedVisitId && (
          <p className="text-sm text-slate-500">No visits available for checklist execution.</p>
        )}

        {selectedVisitId && (
          <>
            <p className="text-sm font-semibold text-slate-700">Active Visit: {selectedVisitId}</p>
            {checklistQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading checklist...</p>}
            {checklistQuery.error && <p className="mt-2 text-sm text-red-600">{(checklistQuery.error as Error).message}</p>}

            <div className="mt-3 grid gap-2">
              {(checklistQuery.data || []).map((item) => {
                const visit = (visitsQuery.data || []).find((v) => v.id === selectedVisitId)
                const executionAllowed = visit ? canExecuteChecklist(role, visit.status) : false
                const recurring = isRecurringTask(item.standard_id)
                return (
                  <div key={item.template_id} className="rounded-lg border border-slate-200 px-3 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800">{item.question_text}</p>
                          {recurring && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700" title="Recurring — requires periodic re-verification">
                              <RotateCcw size={10} /> Recurring
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          <Clock size={10} className="mr-1 inline" />
                          Standard: {item.standard_id}
                        </p>
                      </div>
                    </div>

                    {executionAllowed ? (
                      <div className="mt-2 flex items-center gap-3">
                        <a
                          href={getFieldAppUrl(`/visits/${selectedVisitId}/standard/${item.standard_id}`)}
                          className="text-xs font-semibold text-[var(--brand-primary)] underline"
                        >
                          Execute checklist response
                        </a>
                        <a
                          href={getFieldAppUrl(`/visits/${selectedVisitId}/survey/capture`)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-[var(--brand-primary)]"
                        >
                          <Camera size={12} />
                          Add photo proof
                        </a>
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-slate-500">Checklist execution unavailable for your role or visit state.</p>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </section>

      <Link to="/self-monitoring" className="inline-block text-sm underline">
        Back to self-monitoring dashboard
      </Link>
    </div>
  )
}
